import { useQuery, useMutation } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import * as gpsService from '@/services/gps.service';
import { useAuthStore } from '@/stores/authStore';

const keys = {
  locations: ['gps', 'locations'] as const,
  track: (userId: string, date: string) => ['gps', 'track', userId, date] as const,
};

/** Fetch latest location of all active technicians (auto-refresh 15s) */
export function useTechnicianLocations() {
  return useQuery({
    queryKey: keys.locations,
    queryFn: gpsService.getLocations,
    refetchInterval: 15_000,
  });
}

/** Fetch GPS track for a specific user on a specific day */
export function useGpsTrack(userId: string, date: string) {
  return useQuery({
    queryKey: keys.track(userId, date),
    queryFn: () => gpsService.getTrack(userId, date),
    enabled: !!userId && !!date,
  });
}

/** Send GPS points in batch */
export function useLogGpsPoints() {
  return useMutation({
    mutationFn: gpsService.logPoints,
  });
}

/** Set technician status manually */
export function useSetTechStatus() {
  return useMutation({
    mutationFn: ({ status, workOrderId }: { status: 'available' | 'en_route' | 'working' | 'off_duty'; workOrderId?: string }) =>
      gpsService.setTechStatus(status, workOrderId),
  });
}

/**
 * Geolocation watcher — sends position to backend every `intervalMs` (default 60s).
 * Only active when the user is authenticated. Buffers points and sends in batch.
 */
export function useGpsTracker(intervalMs = 300_000) {
  const token = useAuthStore((s) => s.token);
  const bufferRef = useRef<{ lat: number; lon: number; accuracy?: number; speed?: number; timestamp: string }[]>([]);
  const { mutate } = useLogGpsPoints();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  useEffect(() => {
    if (!token || !navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        bufferRef.current.push({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          speed: pos.coords.speed ?? undefined,
          timestamp: new Date(pos.timestamp).toISOString(),
        });
      },
      () => { /* silently ignore geolocation errors */ },
      { enableHighAccuracy: true, maximumAge: 30_000 },
    );

    const flush = () => {
      if (bufferRef.current.length === 0) return;
      const points = [...bufferRef.current];
      bufferRef.current = [];
      mutateRef.current(points);
    };

    const timer = setInterval(flush, intervalMs);

    return () => {
      navigator.geolocation.clearWatch(watchId);
      clearInterval(timer);
      flush();
    };
  }, [token, intervalMs]);
}
